/**
 * 首版动作库种子数据（§15），结构化、版本化（EXERCISE_LIBRARY_VERSION = 1.0.0）。
 *
 * 铁律（ADR-005 / ADR-007）：
 *   - `lockedUntilMedicalClearance` 为持久化硬锁，true 时无论其它标签都默认不可用，
 *     且不能靠普通编辑/替换解锁（需医疗许可后手动解锁）。
 *   - `forefootLoad` / `balanceDemand` 等是**风险等级**，不是「零负荷」绝对断言；
 *     保守处理、不用 `enabledForCurrentInjury` 硬编码（由规则引擎派生）。
 *   - 重量口径经由 `weightUnitScope` 描述（每只/总重/器械显示值/自重/辅助等），不做隐蔽换算。
 *
 * 阶段 1：仅作为纯前端静态数据；阶段 2 起作为 IndexedDB 种子导入。
 */
import { EXERCISE_LIBRARY_VERSION, type Exercise } from "@jilian/shared";
import type {
  BalanceDemand,
  CompoundType,
  ConditionalExerciseRule,
  EquipmentType,
  IncrementType,
  LoadLevel,
  MovementPattern,
  MountingDifficulty,
  Posture,
  RirValue,
  StabilityLevel,
} from "@jilian/shared";

const SEED_TIME = "2025-01-01T00:00:00.000Z";

type Seed = {
  id: string;
  nameZh: string;
  nameEn: string;
  primaryMuscles: string[];
  secondaryMuscles?: string[];
  movementPattern: MovementPattern;
  equipmentType: EquipmentType;
  posture: Posture;
  compoundType: CompoundType;
  stabilityLevel: StabilityLevel;
  forefootLoad?: LoadLevel;
  heelLoad?: LoadLevel;
  balanceDemand?: BalanceDemand;
  mountingDifficulty?: MountingDifficulty;
  requiresWeightCarrying?: boolean;
  requiresPartner?: boolean;
  failureAllowedLastSet?: boolean;
  defaultSets?: number;
  repMin?: number;
  repMax?: number;
  targetRir?: RirValue;
  restSeconds?: number;
  incrementType: IncrementType;
  defaultIncrementKg?: number | null;
  safetyInstructions: string[];
  contraindicationTags?: string[];
  conditionalRules?: ConditionalExerciseRule[];
  alternativeIds?: string[];
  lockedUntilMedicalClearance?: boolean;
};

function makeExercise(s: Seed): Exercise {
  return {
    id: s.id,
    nameZh: s.nameZh,
    nameEn: s.nameEn,
    primaryMuscles: s.primaryMuscles,
    secondaryMuscles: s.secondaryMuscles ?? [],
    movementPattern: s.movementPattern,
    equipmentType: s.equipmentType,
    posture: s.posture,
    compoundType: s.compoundType,
    stabilityLevel: s.stabilityLevel,
    forefootLoad: s.forefootLoad ?? "none_or_minimal",
    heelLoad: s.heelLoad ?? "none_or_minimal",
    balanceDemand: s.balanceDemand ?? "low",
    mountingDifficulty: s.mountingDifficulty ?? "low",
    requiresWeightCarrying: s.requiresWeightCarrying ?? false,
    requiresPartner: s.requiresPartner ?? false,
    failureAllowedLastSet: s.failureAllowedLastSet ?? false,
    defaultSets: s.defaultSets ?? 3,
    repMin: s.repMin ?? 8,
    repMax: s.repMax ?? 12,
    targetRir: s.targetRir ?? 2,
    restSeconds: s.restSeconds ?? 180,
    incrementType: s.incrementType,
    defaultIncrementKg: s.defaultIncrementKg ?? null,
    safetyInstructions: s.safetyInstructions,
    contraindicationTags: s.contraindicationTags ?? [],
    conditionalRules: s.conditionalRules ?? [],
    alternativeIds: s.alternativeIds ?? [],
    lockedUntilMedicalClearance: s.lockedUntilMedicalClearance ?? false,
    version: 1,
    createdAt: SEED_TIME,
    updatedAt: SEED_TIME,
    reviewStatus: "approved",
    source: "seed",
  };
}

const noForefoot: { forefootLoad: LoadLevel; balanceDemand: BalanceDemand } = {
  forefootLoad: "none_or_minimal",
  balanceDemand: "low",
};

// 医疗许可前锁定提示（统一文案）
const lockNote =
  "医疗许可前锁定：需专业医疗许可解锁后，方可纳入训练计划（硬约束，不能靠替换/编辑绕过）。";

export const EXERCISE_LIBRARY: Exercise[] = [
  // ----------------------------- 胸部 -----------------------------
  makeExercise({
    id: "seated_machine_press",
    nameZh: "坐姿器械推胸",
    nameEn: "Seated Machine Chest Press",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "horizontal_push",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 4,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["坐稳后推，肩胛后收", "脚自然落地，前足无需发力"],
    alternativeIds: ["flat_dumbbell_press", "incline_dumbbell_press", "seated_single_arm_machine_press", "pec_deck"],
  }),
  makeExercise({
    id: "chest_supported_hammer_press",
    nameZh: "胸托式悍马推胸",
    nameEn: "Chest-Supported Hammer Press",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "horizontal_push",
    equipmentType: "machine",
    posture: "chest_supported",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["检查上下器械方式，避免脚掌蹬地借力"],
    conditionalRules: [{ id: "cond-mount-chest-press", conditionType: "requires_safe_mounting", requiredValue: true, failureBehavior: "require_confirmation" }],
    alternativeIds: ["seated_machine_press", "flat_dumbbell_press"],
  }),
  makeExercise({
    id: "barbell_bench_press",
    nameZh: "杠铃平板卧推",
    nameEn: "Barbell Flat Bench Press",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌", "三角肌前束"],
    movementPattern: "horizontal_push",
    equipmentType: "barbell",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 6,
    repMax: 10,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: ["肩胛骨紧贴卧推凳并收紧", "双脚平放地面，不蹬地起桥借力", "下落控制在胸骨中下部，独自训练留有余力"],
    alternativeIds: ["flat_dumbbell_press", "seated_machine_press", "incline_barbell_press", "chest_supported_hammer_press"],
  }),
  makeExercise({
    id: "incline_barbell_press",
    nameZh: "杠铃上斜卧推",
    nameEn: "Incline Barbell Bench Press",
    primaryMuscles: ["胸大肌上部"],
    secondaryMuscles: ["肱三头肌", "三角肌前束"],
    movementPattern: "horizontal_push",
    equipmentType: "barbell",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: ["调整上斜凳角度在 30° 左右", "杠铃平稳下落至锁骨下方，避免耸肩"],
    alternativeIds: ["incline_dumbbell_press", "barbell_bench_press", "seated_machine_press"],
  }),
  makeExercise({
    id: "cable_fly",
    nameZh: "龙门架绳索夹胸",
    nameEn: "Cable Chest Fly",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["三角肌前束"],
    movementPattern: "horizontal_push",
    equipmentType: "cable",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "machine_stack",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["可在坐姿凳上完成以保证足部免负荷", "顶峰环抱收缩胸肌 1 秒，离心匀速还原"],
    alternativeIds: ["pec_deck", "seated_machine_press"],
  }),
  makeExercise({
    id: "dips",
    nameZh: "双杠臂屈伸",
    nameEn: "Dips / Assisted Dips",
    primaryMuscles: ["胸大肌下部", "肱三头肌"],
    secondaryMuscles: ["三角肌前束"],
    movementPattern: "vertical_push",
    equipmentType: "assisted_machine",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 150,
    incrementType: "machine_stack",
    defaultIncrementKg: 5,
    safetyInstructions: ["身体微前倾偏重下胸，垂直偏重三头", "下落至大臂与地面平行即可，避免过度牵拉肩关节"],
    alternativeIds: ["seated_machine_press", "high_stability_triceps_pushdown"],
  }),
  makeExercise({
    id: "push_up",
    nameZh: "标准/跪姿俯卧撑",
    nameEn: "Push-ups",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌", "核心肌群"],
    movementPattern: "horizontal_push",
    equipmentType: "bodyweight",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 20,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "bodyweight",
    defaultIncrementKg: 0,
    safetyInstructions: ["核心收紧呈一条直线，若足部受限可采用跪姿或双手垫高进行"],
    alternativeIds: ["seated_machine_press", "flat_dumbbell_press"],
  }),
  makeExercise({
    id: "flat_dumbbell_press",
    nameZh: "平板哑铃卧推",
    nameEn: "Flat Dumbbell Press",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "horizontal_push",
    equipmentType: "dumbbell",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["肩胛后收，双脚自然踩地但**不得用脚蹬地起桥**", "哑铃全程可控，独自训练不做完全力竭"],
    contraindicationTags: ["禁止脚掌蹬地起桥"],
    alternativeIds: ["seated_machine_press", "incline_dumbbell_press", "chest_supported_hammer_press"],
  }),
  makeExercise({
    id: "incline_dumbbell_press",
    nameZh: "上斜哑铃卧推",
    nameEn: "Incline Dumbbell Press",
    primaryMuscles: ["胸大肌上部"],
    secondaryMuscles: ["肱三头肌", "前束"],
    movementPattern: "horizontal_push",
    equipmentType: "dumbbell",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["上斜凳角度适中，肩部发力不借顶肩", "禁止脚蹬地起桥"],
    contraindicationTags: ["禁止脚掌蹬地起桥"],
    alternativeIds: ["seated_machine_press", "flat_dumbbell_press"],
  }),
  makeExercise({
    id: "pec_deck",
    nameZh: "蝴蝶机夹胸",
    nameEn: "Pec Deck / Rear Delts",
    primaryMuscles: ["胸大肌"],
    movementPattern: "horizontal_push",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭（RIR 0–1）"],
    alternativeIds: ["seated_machine_press", "chest_supported_hammer_press"],
  }),
  makeExercise({
    id: "seated_single_arm_machine_press",
    nameZh: "坐姿单臂器械推胸",
    nameEn: "Seated Single-Arm Machine Press",
    primaryMuscles: ["胸大肌"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "horizontal_push",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["按器械结构评估脚部参与"],
    alternativeIds: ["seated_machine_press", "flat_dumbbell_press"],
  }),

  // ----------------------------- 背部 -----------------------------
  makeExercise({
    id: "wide_grip_lat_pulldown",
    nameZh: "宽握高位下拉",
    nameEn: "Wide-Grip Lat Pulldown",
    primaryMuscles: ["背阔肌"],
    secondaryMuscles: ["肱二头肌"],
    movementPattern: "vertical_pull",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["固定大腿，避免脚掌蹬地借力", "下拉时肩胛后收"],
    alternativeIds: ["neutral_grip_lat_pulldown", "chest_supported_machine_row", "seated_machine_row"],
  }),
  makeExercise({
    id: "neutral_grip_lat_pulldown",
    nameZh: "对握高位下拉",
    nameEn: "Neutral-Grip Lat Pulldown",
    primaryMuscles: ["背阔肌"],
    secondaryMuscles: ["肱二头肌"],
    movementPattern: "vertical_pull",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["固定大腿，避免脚掌蹬地借力"],
    alternativeIds: ["wide_grip_lat_pulldown", "chest_supported_machine_row"],
  }),
  makeExercise({
    id: "chest_supported_machine_row",
    nameZh: "胸托器械划船",
    nameEn: "Chest-Supported Machine Row",
    primaryMuscles: ["背阔肌", "菱形肌"],
    secondaryMuscles: ["肱二头肌"],
    movementPattern: "horizontal_pull",
    equipmentType: "machine",
    posture: "chest_supported",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["优先推荐：避免脚部蹬地借力", "胸部贴紧，肩胛后收"],
    alternativeIds: ["seated_machine_row", "wide_grip_lat_pulldown", "chest_supported_dumbbell_row"],
  }),
  makeExercise({
    id: "seated_machine_row",
    nameZh: "坐姿器械划船",
    nameEn: "Seated Machine Row",
    primaryMuscles: ["背阔肌", "菱形肌"],
    secondaryMuscles: ["肱二头肌"],
    movementPattern: "horizontal_pull",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["仅在不需要前足主动蹬踏时启用"],
    conditionalRules: [{ id: "cond-row-no-forefoot", conditionType: "requires_no_forefoot_drive", requiredValue: true, failureBehavior: "disable" }],
    alternativeIds: ["chest_supported_machine_row", "wide_grip_lat_pulldown"],
  }),
  makeExercise({
    id: "chest_supported_dumbbell_row",
    nameZh: "胸托哑铃划船",
    nameEn: "Chest-Supported Dumbbell Row",
    primaryMuscles: ["背阔肌", "菱形肌"],
    secondaryMuscles: ["肱二头肌"],
    movementPattern: "horizontal_pull",
    equipmentType: "dumbbell",
    posture: "chest_supported",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["根据器械就位方式启用", "禁止高风险力竭"],
    alternativeIds: ["chest_supported_machine_row", "seated_machine_row"],
  }),
  makeExercise({
    id: "barbell_row",
    nameZh: "杠铃俯身划船",
    nameEn: "Barbell Bent-Over Row",
    primaryMuscles: ["背阔肌", "菱形肌", "上背部"],
    secondaryMuscles: ["肱二头肌", "竖脊肌"],
    movementPattern: "horizontal_pull",
    equipmentType: "barbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "medium",
    forefootLoad: "medium",
    heelLoad: "medium",
    balanceDemand: "medium",
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 6,
    repMax: 10,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: ["核心收紧，下背保持中立不弓背", "杠铃贴大腿前侧向肚脐方向拉起"],
    alternativeIds: ["chest_supported_machine_row", "chest_supported_dumbbell_row", "seated_machine_row"],
  }),
  makeExercise({
    id: "dumbbell_single_arm_row",
    nameZh: "哑铃单臂划船",
    nameEn: "Single-Arm Dumbbell Row",
    primaryMuscles: ["背阔肌"],
    secondaryMuscles: ["肱二头肌", "菱形肌"],
    movementPattern: "horizontal_pull",
    equipmentType: "dumbbell",
    posture: "chest_supported",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["单膝或双手支撑在训练凳上，减轻下背与足部压力", "向髋部方向拉动哑铃，挤压背阔肌"],
    alternativeIds: ["chest_supported_dumbbell_row", "chest_supported_machine_row"],
  }),
  makeExercise({
    id: "pull_up",
    nameZh: "引体向上 / 助力引体",
    nameEn: "Pull-ups / Assisted Pull-ups",
    primaryMuscles: ["背阔肌"],
    secondaryMuscles: ["肱二头肌", "上背部"],
    movementPattern: "vertical_pull",
    equipmentType: "assisted_machine",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    defaultIncrementKg: 5,
    safetyInstructions: ["下沉并后收肩胛骨，沉肩启动", "拉至下巴过杠，离心下落完全拉伸"],
    alternativeIds: ["wide_grip_lat_pulldown", "neutral_grip_lat_pulldown"],
  }),
  makeExercise({
    id: "face_pull",
    nameZh: "绳索面拉",
    nameEn: "Cable Face Pull",
    primaryMuscles: ["三角肌后束", "上背部"],
    secondaryMuscles: ["斜方肌中下部", "肩袖肌群"],
    movementPattern: "horizontal_pull",
    equipmentType: "cable",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 15,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "machine_stack",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["可在坐姿下完成以完全免去前足负荷", "将绳索向面部拉开，同时做外旋动作"],
    alternativeIds: ["reverse_pec_deck", "chest_supported_rear_delt_fly"],
  }),
  makeExercise({
    id: "machine_straight_arm_pulldown",
    nameZh: "器械直臂下拉",
    nameEn: "Machine Straight-Arm Pulldown",
    primaryMuscles: ["背阔肌"],
    movementPattern: "vertical_pull",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅当设备允许坐姿或高度稳定时使用"],
    conditionalRules: [{ id: "cond-straight-arm", conditionType: "requires_seated_setup", requiredValue: true, failureBehavior: "disable" }],
    alternativeIds: ["wide_grip_lat_pulldown", "neutral_grip_lat_pulldown"],
  }),

  makeExercise({
    id: "barbell_overhead_press",
    nameZh: "杠铃推举",
    nameEn: "Barbell Overhead Press (OHP)",
    primaryMuscles: ["三角肌前束"],
    secondaryMuscles: ["三角肌中束", "肱三头肌", "核心肌群"],
    movementPattern: "vertical_push",
    equipmentType: "barbell",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 6,
    repMax: 10,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["建议采用坐姿靠背推举以完全保护前足与腰椎", "核心收紧，杠铃平稳推过头顶"],
    alternativeIds: ["seated_dumbbell_shoulder_press", "seated_machine_shoulder_press"],
  }),
  makeExercise({
    id: "cable_lateral_raise",
    nameZh: "绳索侧平举",
    nameEn: "Cable Lateral Raise",
    primaryMuscles: ["三角肌中束"],
    movementPattern: "other",
    equipmentType: "cable",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 18,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "machine_stack",
    defaultIncrementKg: 1.25,
    safetyInstructions: ["绳索阻力全程均匀，可在坐姿下完成", "肘部微屈带动手臂侧向抬起至与肩同高"],
    alternativeIds: ["seated_dumbbell_lateral_raise", "machine_lateral_raise"],
  }),
  makeExercise({
    id: "seated_machine_shoulder_press",
    nameZh: "坐姿器械推肩",
    nameEn: "Seated Machine Shoulder Press",
    primaryMuscles: ["三角肌前束"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "vertical_push",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: ["坐稳，避免借力蹬腿", "不主动力竭"],
    alternativeIds: ["seated_dumbbell_shoulder_press", "seated_dumbbell_lateral_raise"],
  }),
  makeExercise({
    id: "seated_dumbbell_shoulder_press",
    nameZh: "坐姿哑铃推举（带靠背）",
    nameEn: "Seated Dumbbell Shoulder Press",
    primaryMuscles: ["三角肌前束"],
    secondaryMuscles: ["肱三头肌"],
    movementPattern: "vertical_push",
    equipmentType: "dumbbell",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["靠背支撑，禁止完全力竭"],
    alternativeIds: ["seated_machine_shoulder_press", "seated_dumbbell_lateral_raise"],
  }),
  makeExercise({
    id: "seated_dumbbell_lateral_raise",
    nameZh: "坐姿哑铃侧平举",
    nameEn: "Seated Dumbbell Lateral Raise",
    primaryMuscles: ["三角肌中束"],
    movementPattern: "other",
    equipmentType: "dumbbell",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["仅最后一个工作组允许接近力竭（RIR 0–1）"],
    alternativeIds: ["machine_lateral_raise", "reverse_pec_deck"],
  }),
  makeExercise({
    id: "machine_lateral_raise",
    nameZh: "器械侧平举",
    nameEn: "Machine Lateral Raise",
    primaryMuscles: ["三角肌中束"],
    movementPattern: "other",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭"],
    alternativeIds: ["seated_dumbbell_lateral_raise", "reverse_pec_deck"],
  }),
  makeExercise({
    id: "reverse_pec_deck",
    nameZh: "反向蝴蝶机（后束）",
    nameEn: "Reverse Pec Deck",
    primaryMuscles: ["三角肌后束"],
    movementPattern: "posterior_delt",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭"],
    alternativeIds: ["chest_supported_rear_delt_fly", "machine_lateral_raise"],
  }),
  makeExercise({
    id: "chest_supported_rear_delt_fly",
    nameZh: "胸托后束飞鸟",
    nameEn: "Chest-Supported Rear Delt Fly",
    primaryMuscles: ["三角肌后束"],
    movementPattern: "posterior_delt",
    equipmentType: "dumbbell",
    posture: "chest_supported",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["根据器械结构启用"],
    alternativeIds: ["reverse_pec_deck", "machine_lateral_raise"],
  }),

  // ----------------------------- 手臂 -----------------------------
  makeExercise({
    id: "preacher_curl",
    nameZh: "牧师凳弯举",
    nameEn: "Preacher Curl",
    primaryMuscles: ["肱二头肌"],
    movementPattern: "arm_curl",
    equipmentType: "bench",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["仅最后一个工作组允许接近力竭"],
    alternativeIds: ["machine_preacher_curl", "seated_dumbbell_alt_curl", "incline_dumbbell_curl"],
  }),
  makeExercise({
    id: "machine_preacher_curl",
    nameZh: "器械托臂弯举",
    nameEn: "Machine Preacher Curl",
    primaryMuscles: ["肱二头肌"],
    movementPattern: "arm_curl",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭"],
    alternativeIds: ["preacher_curl", "seated_cable_biceps_curl"],
  }),
  makeExercise({
    id: "seated_dumbbell_alt_curl",
    nameZh: "坐姿哑铃交替弯举",
    nameEn: "Seated Alternating Dumbbell Curl",
    primaryMuscles: ["肱二头肌"],
    movementPattern: "arm_curl",
    equipmentType: "dumbbell",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["坐姿完成，避免用身体借力"],
    alternativeIds: ["preacher_curl", "incline_dumbbell_curl", "seated_cable_biceps_curl"],
  }),
  makeExercise({
    id: "incline_dumbbell_curl",
    nameZh: "上斜凳哑铃弯举",
    nameEn: "Incline Dumbbell Curl",
    primaryMuscles: ["肱二头肌"],
    movementPattern: "arm_curl",
    equipmentType: "dumbbell",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["上斜凳支撑背部，避免借力"],
    alternativeIds: ["seated_dumbbell_alt_curl", "preacher_curl"],
  }),
  makeExercise({
    id: "seated_cable_biceps_curl",
    nameZh: "坐姿绳索二头弯举",
    nameEn: "Seated Cable Biceps Curl",
    primaryMuscles: ["肱二头肌"],
    movementPattern: "arm_curl",
    equipmentType: "cable",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["根据脚部状态与器械位置判断，不默认安全"],
    alternativeIds: ["machine_preacher_curl", "seated_dumbbell_alt_curl"],
  }),
  makeExercise({
    id: "high_stability_triceps_pushdown",
    nameZh: "高稳定绳索三头下压",
    nameEn: "High-Stability Cable Triceps Pushdown",
    primaryMuscles: ["肱三头肌"],
    movementPattern: "arm_extension",
    equipmentType: "cable",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭", "根据脚部状态与器械位置判断"],
    alternativeIds: ["seated_machine_triceps_extension", "seated_dumbbell_overhead_extension"],
  }),
  makeExercise({
    id: "seated_machine_triceps_extension",
    nameZh: "坐姿器械三头伸展",
    nameEn: "Seated Machine Triceps Extension",
    primaryMuscles: ["肱三头肌"],
    movementPattern: "arm_extension",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["仅最后一个工作组允许接近力竭"],
    alternativeIds: ["high_stability_triceps_pushdown", "seated_dumbbell_overhead_extension"],
  }),
  makeExercise({
    id: "barbell_biceps_curl",
    nameZh: "杠铃弯举",
    nameEn: "Barbell Biceps Curl",
    primaryMuscles: ["肱二头肌"],
    secondaryMuscles: ["前臂肌群"],
    movementPattern: "arm_curl",
    equipmentType: "barbell",
    posture: "standing",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "barbell_total",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["大臂贴紧躯干两侧，避免身体前后晃动借力", "顶峰收缩二头肌，下放时充分离心控制"],
    alternativeIds: ["preacher_curl", "seated_dumbbell_alt_curl", "seated_cable_biceps_curl"],
  }),
  makeExercise({
    id: "skull_crusher",
    nameZh: "仰卧臂屈伸 (碎颅者)",
    nameEn: "Lying Triceps Extension (Skull Crusher)",
    primaryMuscles: ["肱三头肌长头"],
    secondaryMuscles: ["肱三头肌内侧/外侧头"],
    movementPattern: "arm_extension",
    equipmentType: "barbell",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "barbell_total",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["仰卧在平凳上，大臂保持竖直微向后倾斜", "弯曲肘部将杠铃降至额头或头顶上方，平稳推起"],
    alternativeIds: ["high_stability_triceps_pushdown", "seated_machine_triceps_extension", "seated_dumbbell_overhead_extension"],
  }),
  makeExercise({
    id: "seated_dumbbell_overhead_extension",
    nameZh: "坐姿哑铃过头臂屈伸",
    nameEn: "Seated Dumbbell Overhead Extension",
    primaryMuscles: ["肱三头肌"],
    movementPattern: "arm_extension",
    equipmentType: "dumbbell",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: ["坐姿完成，哑铃全程可控"],
    alternativeIds: ["high_stability_triceps_pushdown", "seated_machine_triceps_extension", "skull_crusher"],
  }),

  // --------------------- 下肢孤立（当前可用） ---------------------
  makeExercise({
    id: "hip_thrust",
    nameZh: "杠铃/器械臀推",
    nameEn: "Hip Thrust",
    primaryMuscles: ["臀大肌"],
    secondaryMuscles: ["腘绳肌", "核心肌群"],
    movementPattern: "other",
    equipmentType: "barbell",
    posture: "lying",
    compoundType: "compound",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: ["上背部靠在长凳上，全脚掌平稳着地或用足跟发力", "顶峰充分夹紧臀部，避免下背过度反弓"],
    alternativeIds: ["seated_hip_abduction", "seated_leg_curl"],
  }),
  makeExercise({
    id: "seated_leg_extension",
    nameZh: "坐姿腿屈伸",
    nameEn: "Seated Leg Extension",
    primaryMuscles: ["股四头肌"],
    movementPattern: "knee_extension",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 150,
    incrementType: "machine_stack",
    safetyInstructions: ["确认滚轴位置、脚趾放松", "出现伤处不适立即停止"],
    alternativeIds: ["seated_leg_curl", "seated_hip_abduction", "hip_thrust"],
  }),
  makeExercise({
    id: "prone_leg_curl",
    nameZh: "俯卧腿弯举",
    nameEn: "Prone Leg Curl",
    primaryMuscles: ["腘绳肌"],
    movementPattern: "knee_flexion",
    equipmentType: "machine",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 150,
    incrementType: "machine_stack",
    safetyInstructions: ["确认滚轴不会对伤处造成压力"],
    alternativeIds: ["seated_leg_curl", "seated_leg_extension", "hip_thrust"],
  }),
  makeExercise({
    id: "seated_leg_curl",
    nameZh: "坐姿腿弯举",
    nameEn: "Seated Leg Curl",
    primaryMuscles: ["腘绳肌"],
    movementPattern: "knee_flexion",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 150,
    incrementType: "machine_stack",
    safetyInstructions: ["根据器械脚部接触位置启用"],
    alternativeIds: ["prone_leg_curl", "seated_leg_extension", "hip_thrust"],
  }),
  makeExercise({
    id: "seated_hip_abduction",
    nameZh: "坐姿髋外展",
    nameEn: "Seated Hip Abduction",
    primaryMuscles: ["臀中肌"],
    movementPattern: "hip_abduction",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 20,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["低足部参与，保持坐姿稳定"],
    alternativeIds: ["seated_hip_adduction", "hip_thrust"],
  }),
  makeExercise({
    id: "seated_hip_adduction",
    nameZh: "坐姿髋内收",
    nameEn: "Seated Hip Adduction",
    primaryMuscles: ["髋内收肌"],
    movementPattern: "hip_adduction",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 20,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: ["非默认补充动作，保持坐姿稳定"],
    alternativeIds: ["seated_hip_abduction"],
  }),

  // ----------------------------- 核心 -----------------------------
  makeExercise({
    id: "hanging_leg_raise",
    nameZh: "悬垂举腿 / 罗马椅举腿",
    nameEn: "Hanging / Captain's Chair Leg Raise",
    primaryMuscles: ["腹直肌下部"],
    secondaryMuscles: ["髂腰肌", "前臂抓握力"],
    movementPattern: "trunk_flexion",
    equipmentType: "assisted_machine",
    posture: "standing",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "bodyweight",
    defaultIncrementKg: 0,
    safetyInstructions: ["可在罗马椅手臂支撑下完成，避免身体晃动", "骨盆向上卷起以充分刺激下腹"],
    alternativeIds: ["seated_machine_crunch", "lying_crunch_no_foot_fix"],
  }),
  makeExercise({
    id: "plank",
    nameZh: "平板支撑",
    nameEn: "Plank",
    primaryMuscles: ["核心肌群", "腹横肌"],
    secondaryMuscles: ["三角肌", "臀部肌群"],
    movementPattern: "other",
    equipmentType: "bodyweight",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 30, // 30-60 秒
    repMax: 60,
    targetRir: 2,
    restSeconds: 60,
    incrementType: "bodyweight",
    defaultIncrementKg: 0,
    safetyInstructions: ["前臂撑地，身体呈一直线，骨盆微后倾收紧臀部与腹部", "足部不适时可用双膝跪地跪姿平板支撑"],
    alternativeIds: ["seated_machine_crunch", "lying_crunch_no_foot_fix"],
  }),
  makeExercise({
    id: "ab_wheel",
    nameZh: "健腹轮",
    nameEn: "Ab Wheel Rollout",
    primaryMuscles: ["腹直肌", "腹部核心"],
    secondaryMuscles: ["背阔肌", "肩部"],
    movementPattern: "trunk_flexion",
    equipmentType: "bodyweight",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "bodyweight",
    defaultIncrementKg: 0,
    safetyInstructions: ["采用跪姿进行，膝盖垫软垫保护", "向前推出时下背部不可塌陷反弓，靠腹肌力量拉回"],
    alternativeIds: ["seated_machine_crunch", "lying_crunch_no_foot_fix", "hanging_leg_raise"],
  }),
  makeExercise({
    id: "seated_machine_crunch",
    nameZh: "坐姿器械卷腹",
    nameEn: "Seated Machine Crunch",
    primaryMuscles: ["腹直肌"],
    movementPattern: "trunk_flexion",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "isolation",
    stabilityLevel: "high",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 20,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "machine_stack",
    safetyInstructions: ["保持坐姿稳定"],
    alternativeIds: ["lying_crunch_no_foot_fix"],
  }),
  makeExercise({
    id: "lying_crunch_no_foot_fix",
    nameZh: "仰卧卷腹（无需脚部固定）",
    nameEn: "Lying Crunch (No Foot Fixation)",
    primaryMuscles: ["腹直肌"],
    movementPattern: "trunk_flexion",
    equipmentType: "bodyweight",
    posture: "lying",
    compoundType: "isolation",
    stabilityLevel: "medium",
    ...noForefoot,
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 12,
    repMax: 20,
    targetRir: 2,
    restSeconds: 90,
    incrementType: "bodyweight",
    safetyInstructions: ["不需要脚部固定或前足支撑"],
    alternativeIds: ["seated_machine_crunch"],
  }),

  // ------------- 医疗许可前硬锁（锁定，不进入默认计划） -------------
  makeExercise({
    id: "barbell_squat",
    nameZh: "深蹲",
    nameEn: "Barbell Back Squat",
    primaryMuscles: ["股四头肌", "臀大肌"],
    secondaryMuscles: ["腘绳肌", "核心"],
    movementPattern: "other",
    equipmentType: "barbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "low",
    forefootLoad: "high",
    heelLoad: "high",
    balanceDemand: "high",
    mountingDifficulty: "medium",
    requiresWeightCarrying: true,
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 5,
    repMax: 8,
    targetRir: 2,
    restSeconds: 210,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "barbell_deadlift",
    nameZh: "硬拉",
    nameEn: "Barbell Deadlift",
    primaryMuscles: ["臀大肌", "腘绳肌"],
    secondaryMuscles: ["背部", "核心"],
    movementPattern: "other",
    equipmentType: "barbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "low",
    forefootLoad: "high",
    heelLoad: "high",
    balanceDemand: "medium",
    mountingDifficulty: "medium",
    requiresWeightCarrying: true,
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 5,
    repMax: 8,
    targetRir: 2,
    restSeconds: 210,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "romanian_deadlift",
    nameZh: "罗马尼亚硬拉",
    nameEn: "Romanian Deadlift",
    primaryMuscles: ["腘绳肌", "臀大肌"],
    secondaryMuscles: ["背部"],
    movementPattern: "other",
    equipmentType: "barbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "low",
    forefootLoad: "high",
    heelLoad: "high",
    balanceDemand: "medium",
    mountingDifficulty: "medium",
    requiresWeightCarrying: true,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "barbell_total",
    defaultIncrementKg: 5,
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "walking_lunge",
    nameZh: "箭步蹲",
    nameEn: "Walking Lunge",
    primaryMuscles: ["股四头肌", "臀大肌"],
    movementPattern: "other",
    equipmentType: "dumbbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "low",
    forefootLoad: "high",
    heelLoad: "medium",
    balanceDemand: "high",
    mountingDifficulty: "medium",
    requiresWeightCarrying: true,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 210,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "bulgarian_split_squat",
    nameZh: "保加利亚分腿蹲",
    nameEn: "Bulgarian Split Squat",
    primaryMuscles: ["股四头肌", "臀大肌"],
    movementPattern: "other",
    equipmentType: "dumbbell",
    posture: "standing",
    compoundType: "compound",
    stabilityLevel: "low",
    forefootLoad: "high",
    heelLoad: "medium",
    balanceDemand: "high",
    mountingDifficulty: "medium",
    requiresWeightCarrying: true,
    failureAllowedLastSet: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 210,
    incrementType: "per_dumbbell",
    defaultIncrementKg: 2.5,
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "leg_press",
    nameZh: "腿举",
    nameEn: "Leg Press",
    primaryMuscles: ["股四头肌", "臀大肌"],
    movementPattern: "other",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "medium",
    forefootLoad: "high",
    heelLoad: "medium",
    balanceDemand: "low",
    mountingDifficulty: "low",
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "hack_squat",
    nameZh: "哈克深蹲",
    nameEn: "Hack Squat",
    primaryMuscles: ["股四头肌", "臀大肌"],
    movementPattern: "other",
    equipmentType: "machine",
    posture: "seated",
    compoundType: "compound",
    stabilityLevel: "medium",
    forefootLoad: "high",
    heelLoad: "medium",
    balanceDemand: "low",
    mountingDifficulty: "low",
    failureAllowedLastSet: false,
    defaultSets: 4,
    repMin: 8,
    repMax: 12,
    targetRir: 2,
    restSeconds: 180,
    incrementType: "machine_stack",
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力", "下肢复合负重"],
    lockedUntilMedicalClearance: true,
  }),
  makeExercise({
    id: "standing_calf_raise",
    nameZh: "站姿提踵",
    nameEn: "Standing Calf Raise",
    primaryMuscles: ["小腿腓肠肌"],
    movementPattern: "other",
    equipmentType: "machine",
    posture: "standing",
    compoundType: "isolation",
    stabilityLevel: "medium",
    forefootLoad: "high",
    heelLoad: "low",
    balanceDemand: "medium",
    mountingDifficulty: "low",
    failureAllowedLastSet: true,
    defaultSets: 3,
    repMin: 10,
    repMax: 15,
    targetRir: 2,
    restSeconds: 120,
    incrementType: "machine_stack",
    safetyInstructions: [lockNote],
    contraindicationTags: ["前足受力"],
    lockedUntilMedicalClearance: true,
  }),
];

/** 动作库中的硬锁动作（用于展示「锁定」说明）。 */
export const LOCKED_EXERCISE_IDS = new Set(
  EXERCISE_LIBRARY.filter((e) => e.lockedUntilMedicalClearance).map((e) => e.id),
);

/** 查询时用：id → Exercise。 */
export const EXERCISE_BY_ID = new Map(EXERCISE_LIBRARY.map((e) => [e.id, e]));

export { EXERCISE_LIBRARY_VERSION };
